/* eslint-disable @typescript-eslint/no-explicit-any */
import { shallowDiffer } from "./diff";

const CACHE_SIZE = 50000;
const CACHE_EXPIRE_TIME = 300000; // (1000 * 60 * 5), 5 minutes

/**
 * Creates a memoized function which holds previous execution results of given function on a cache object.
 * Cache gets flushed when it reaches given limit or expiration time overs.
 *
 * @param fn: Function to be memoized.
 * @param argumentKeyCreationAlgo(args): Optional algorithm to control the caching behaviour.
 * @param cacheSize: Number of elements that limits the cache size. Defaults to 50.000.
 * @param cacheExpireTime: Number of ms that limits the cache's expire time. Defaults to 5 min.
 */

function memoize<T extends(...args: any[]) => any, R>(
    fn: T,
    argumentKeyCreationAlgo?: (args: Parameters<T>) => string,
    cacheSize: number = CACHE_SIZE,
    cacheExpireTime: number = CACHE_EXPIRE_TIME
) {
    const cache = new Map<string, { value: ReturnType<T>; timestamp: number; }>();
    const startTime = Date.now();

    return function memoizedFn<This>(this: This, ...args: Parameters<T>) {
        try {
            // When cache reaches its limit or expire time overs, it is flushed.
            if (cache.size > cacheSize || Date.now() > (startTime + cacheExpireTime)) {
                cache.clear();
            }

            const argumentKey = argumentKeyCreationAlgo
                ? argumentKeyCreationAlgo(args)
                : JSON.stringify(args);

            if (cache.has(argumentKey)) {
                return cache.get(argumentKey);
            }

            const result = fn.apply(this, args);
            cache.set(argumentKey, result);

            return result;
        } catch (e) {
            console.warn("Trying to memoize improper arguments. So, this is no-op.", e);

            return fn.apply(this, args);
        }
    };
}

/**
 * Memoizes a function that takes a single args object
 *
 * Caches results based on shallow comparison for each argument in the args object
 *
 * @returns memoized function
 */
function memoizeLastResult<T extends Record<string, unknown>, R>(
    fn: (args: T) => R
): (args: T) => R {
    const cache: {
        lastResult: R | null;
        lastArgs: T | null;
    } = {
        lastResult: null,
        lastArgs: null
    };

    return function (args: T): R {
        const areSameArgs = !!cache.lastArgs && !shallowDiffer(cache.lastArgs, args);

        if (areSameArgs && cache.lastResult !== null) {
            return cache.lastResult;
        }

        const result = fn(args);

        cache.lastResult = result;
        cache.lastArgs = args;

        return result;
    };
}

/**
 * Memoizes a function that takes a single arguments object
 * When the dependencies change, the cache is invalidated
 *
 * @returns memoized function that takes an additional array of dependencies
 */
function memoizeWithDependencies<T extends Record<string, unknown>, R>(
    fn: (args: T) => R
): (args: T, dependencies: unknown[]) => R {
    const cache: {
        lastResult: R | null;
        lastDependencies: unknown[] | null;
    } = {
        lastResult: null,
        lastDependencies: null
    };

    return function (args: T, dependencies: unknown[]): R {
        const haveDependenciesChanged = !cache.lastDependencies ||
            dependencies.some((dep, index) => dep !== cache.lastDependencies![index]);

        if (!haveDependenciesChanged && cache.lastResult !== null) {
            return cache.lastResult;
        }

        const result = fn(args);

        cache.lastResult = result;
        cache.lastDependencies = dependencies;

        return result;
    };
}

export {
    memoize,
    memoizeLastResult,
    memoizeWithDependencies
};
